import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook para gestionar la carga, sincronización y normalización de detalles de un ticket,
 * así como el marcado de lectura en el almacenamiento local y eventos del sistema.
 */
export const useTicketDetail = ({ ticket, token, onTicketDetailLoaded }) => {
  const [ticketDetail, setTicketDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(true);
  const lastFetchedTicketIdRef = useRef(null);

  const activeTicketId = ticket?.idTicket || ticket?.id;

  const onTicketDetailLoadedRef = useRef(onTicketDetailLoaded);
  onTicketDetailLoadedRef.current = onTicketDetailLoaded;

  const fetchTicketDetail = useCallback(async (silent = false) => {
    if (!activeTicketId || activeTicketId === 'undefined' || !token) {
      setIsLoadingDetail(false);
      return null;
    }

    if (!silent && !ticketDetail) setIsLoadingDetail(true);

    try {
      const response = await fetch(`/api/SupportEngine/GetTicketDetail/${activeTicketId}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;
        if (data) {
          setTicketDetail(data);
          if (onTicketDetailLoadedRef.current) {
            onTicketDetailLoadedRef.current(data);
          }
          return data;
        }
      }
    } catch (err) {
      console.error('Error al cargar detalle del ticket:', err);
    } finally {
      setIsLoadingDetail(false);
    }
    return null;
  }, [activeTicketId, token, ticketDetail]);

  const markCurrentAsRead = useCallback((ticketIdToMark) => {
    const idNum = Number(ticketIdToMark);
    if (!idNum) return;

    try {
      const stored = localStorage.getItem('support_engine_read_tickets');
      const currentMap = stored ? JSON.parse(stored) : {};
      currentMap[idNum] = new Date().toISOString();
      localStorage.setItem('support_engine_read_tickets', JSON.stringify(currentMap));
    } catch (e) {
      console.warn('Error al marcar ticket leído en localStorage:', e);
    }

    window.dispatchEvent(
      new CustomEvent('akasia:support:ticket_read', {
        detail: { idTicket: idNum }
      })
    );
  }, []);

  useEffect(() => {
    if (!activeTicketId || activeTicketId === 'undefined' || !token) return;

    const normalizedId = String(activeTicketId);
    if (lastFetchedTicketIdRef.current === normalizedId) return;
    lastFetchedTicketIdRef.current = normalizedId;

    markCurrentAsRead(activeTicketId);
    fetchTicketDetail();

    return () => {
      markCurrentAsRead(activeTicketId);
    };
  }, [activeTicketId, token, fetchTicketDetail, markCurrentAsRead]);

  // Marcar como leído cuando el usuario efectivamente enfoca o hace visible la pestaña del chat
  useEffect(() => {
    const handleFocus = () => {
      if (activeTicketId && typeof document !== 'undefined' && !document.hidden) {
        markCurrentAsRead(activeTicketId);
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [activeTicketId, markCurrentAsRead]);

  // Propiedades derivadas y normalizadas
  const customerName = ticketDetail?.vCustomerName || ticket?.customerName;
  const ticketPhone = ticketDetail?.vTelefono || ticket?.vTelefono || ticket?.phone;
  const iEstadoTicket = ticketDetail?.iEstadoTicket !== undefined ? ticketDetail.iEstadoTicket : ticket?.iEstadoTicket;
  const idCatNivelSoporte = ticketDetail?.idCatNivelSoporte !== undefined ? ticketDetail.idCatNivelSoporte : ticket?.idCatNivelSoporte;
  const idCatArea = ticketDetail?.idCatArea || ticket?.idCatArea;
  const createdDate = ticketDetail?.dCreatedDate || ticket?.createdDate || ticket?.dCreatedDate;
  const ultimaActividad = ticketDetail?.dUltimaActividad || ticket?.ultimaActividad || ticket?.dUltimaActividad;
  const idClienteAkasia = ticketDetail?.idClienteAkasia || ticket?.idClienteAkasia;
  const vCreatedUser = ticketDetail?.vCreatedUser || ticket?.vCreatedUser;
  const idUsuarioAsignado = ticketDetail?.idUsuarioAsignado !== undefined ? ticketDetail.idUsuarioAsignado : ticket?.idUsuarioAsignado;
  const vNombreUsuarioAsignado = ticketDetail?.vNombreUsuarioAsignado || ticket?.vNombreUsuarioAsignado;
  const vMotivoCerrado = ticketDetail?.vMotivoCerrado || ticket?.vMotivoCerrado;
  const iTiempoRespuestaMinutos = ticketDetail?.iTiempoRespuestaMinutos !== undefined 
    ? ticketDetail.iTiempoRespuestaMinutos 
    : ticket?.iTiempoRespuestaMinutos;

  const initials = customerName 
    ? customerName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() 
    : '';

  const activeTicket = {
    ...ticket,
    ...(ticketDetail || {}),
    id: ticketDetail?.idTicket || ticket.id || ticket.idTicket,
    idTicket: ticketDetail?.idTicket || ticket.idTicket || ticket.id,
    customerName,
    initials,
    phone: ticketPhone,
    ticketPhone,
    iEstadoTicket,
    idCatNivelSoporte,
    idCatArea,
    createdDate,
    dCreatedDate: createdDate,
    ultimaActividad,
    dUltimaActividad: ultimaActividad,
    idClienteAkasia,
    vCreatedUser,
    idUsuarioAsignado,
    vNombreUsuarioAsignado,
    vMotivoCerrado,
    iTiempoRespuestaMinutos,
    dFechaCierre: ticketDetail?.dFechaCierre || ticket?.dFechaCierre,
    iTiempoResolucionMinutos: ticketDetail?.iTiempoResolucionMinutos !== undefined ? ticketDetail.iTiempoResolucionMinutos : ticket?.iTiempoResolucionMinutos,
    iTiempoCompletadoMinutos: ticketDetail?.iTiempoCompletadoMinutos !== undefined ? ticketDetail.iTiempoCompletadoMinutos : (ticketDetail?.iTiempoResolucionMinutos ?? ticket?.iTiempoResolucionMinutos),
    idTicketDestino: ticketDetail?.idTicketDestino || ticket?.idTicketDestino
  };

  return {
    activeTicket,
    ticketDetail,
    setTicketDetail,
    isLoadingDetail,
    fetchTicketDetail,
    markCurrentAsRead
  };
};
